import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import { FeatureFlagsContext } from 'components/shared/feature-flags';

import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import TabsRouter from 'components/partials/tabs-router/tabs-router';
import { getStatusLabel } from 'components/partials/table-component/helpers/get-status-label';

import UsersTab from 'containers/admin/clients/client/tabs-components/users-tab';
import ProfileTab from 'containers/admin/clients/client/tabs-components/profile-tab';
import FilesTab from 'containers/admin/clients/client/tabs-components/files-tab/files-tab';
import GoalsTab from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab';
import CriticalElementsTab from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-tab';
import MonitoringTab from 'containers/admin/clients/client/tabs-components/monitoring-tab/monitoring-tab';
import InsightsTab from 'containers/admin/clients/client/tabs-components/insights-tab/insights-tab';
import CriticalPopulationsTab from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations-tab';
import AidStudioTab from 'containers/admin/clients/client/tabs-components/aid-studio-tab';

import { CLIENTS_STATUSES } from 'containers/admin/clients/clients.constants';
import { ClientProfileDeleteBtn } from 'containers/admin/clients/client/client-profile-delete-btn';

import { useClient } from 'hooks/api/use-clients';
import type { ClientRecord } from 'hooks/api/use-clients';
import { useCurrentUser } from 'hooks/api/use-current-user';
import { useSelectedClient } from 'hooks/use-selected-client';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import ProductsTab from './tabs-components/products-tab/products-tab';
import ReportsTab from './tabs-components/reports-tab/reports-tab';

import { getCurrentYear } from 'helpers/date.helpers';
import { canAccessCriticalPopulations } from 'helpers/critical-populations-access';

interface FeatureFlagsContextValue {
  featureFlags: Record<string, boolean>;
}

interface ClientDetailsRecord extends Partial<ClientRecord> {
  status?: string;
}

const ClientDetails = (): React.JSX.Element => {
  const [client, setClient] = useState<ClientDetailsRecord>(
    {} as ClientDetailsRecord
  );

  const location = useLocation();
  const params = useParams<{ id: string }>();

  const currentYear = getCurrentYear();
  const navigate = useNavigate();

  const { featureFlags } = useContext(
    FeatureFlagsContext as unknown as React.Context<FeatureFlagsContextValue>
  );

  const currentTab = location?.pathname.split('/')[4] || '';
  const currentUser = useCurrentUser();
  const { selectClient, selectedClient } = useSelectedClient();

  const clientId = params.id ?? '';

  const {
    data: fetchedClient,
    loading,
    error: clientError,
  } = useClient(clientId);

  useEffect(() => {
    if (clientError) {
      navigate('/admin/clients');
    }
  }, [clientError]);

  useEffect(() => {
    if (fetchedClient) {
      setClient(fetchedClient as ClientDetailsRecord);
      if (!selectedClient || selectedClient.id?.toString() !== clientId) {
        selectClient(fetchedClient as never);
      }
    }
  }, [fetchedClient]);

  const getYearFromPath = (): number => {
    const pathParts = location.pathname.split('/');

    const goalsIndex = pathParts.indexOf('goals');

    if (goalsIndex !== -1 && pathParts.length > goalsIndex + 1) {
      const year = pathParts[goalsIndex + 1];

      return year && !Number.isNaN(Number.parseInt(year, 10))
        ? Number.parseInt(year, 10)
        : currentYear;
    }

    return currentYear;
  };

  const selectedYear = getYearFromPath();

  const tabLink = (to: string): string =>
    to === 'goals'
      ? `/admin/clients/${clientId}/${to}/${selectedYear}`
      : `/admin/clients/${clientId}/${to}`;

  const tabsData = [
    {
      key: `${clientId}-users`,
      link: tabLink('users'),
      component: <UsersTab {...{ clientId }} />,
      label: 'Users',
    },
    {
      key: `${clientId}-files`,
      link: tabLink('files'),
      component: <FilesTab {...{ clientId }} />,
      label: 'Files',
    },
    {
      key: `${clientId}-insights`,
      link: tabLink('insights'),
      component: <InsightsTab {...{ clientId }} />,
      label: 'Insights',
    },
    {
      key: `${clientId}-goals`,
      link: tabLink('goals'),
      component: <GoalsTab {...{ clientId, selectedYear }} />,
      label: 'Goals',
    },
    {
      key: `${clientId}-critical-elements`,
      link: tabLink('critical-elements'),
      component: <CriticalElementsTab {...{ clientId }} />,
      label: 'Critical Elements',
    },
    canAccessCriticalPopulations(currentUser.data, featureFlags) && {
      key: `${clientId}-critical-populations`,
      link: tabLink('critical-populations'),
      component: <CriticalPopulationsTab {...{ clientId }} />,
      label: 'Critical Elements v2',
    },
    featureFlags['critical-elements-monitoring'] && {
      key: `${clientId}-monitoring`,
      link: tabLink('monitoring'),
      component: <MonitoringTab {...{ clientId }} />,
      label: 'Monitoring',
    },
    {
      key: `${clientId}-products`,
      link: tabLink('products'),
      component: <ProductsTab {...{ clientId }} />,
      label: 'Products',
    },
    {
      key: `${clientId}-reports`,
      link: tabLink('reports'),
      component: <ReportsTab {...{ clientId }} />,
      label: 'Reports',
    },
    featureFlags['tab-fao'] && {
      key: `${clientId}-aid-studio`,
      link: tabLink('aid-studio'),
      component: <AidStudioTab clientId={Number(clientId)} />,
      label: 'Aid Studio',
    },
    {
      key: `${clientId}-profile`,
      link: tabLink('profile'),
      component: <ProfileTab {...{ client: client as never, setClient }} />,
      label: 'Profile',
    },
  ].filter(Boolean);

  const label = getStatusLabel(client.status ?? '', CLIENTS_STATUSES);

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      {loading ? (
        <Loader {...{ loading: loading }} />
      ) : (
        <>
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Clients', href: '/admin/clients' },
              { label: client.name ?? '', isActive: true },
            ]}
          />
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-4">
              <Heading variant="h1" data-testid="client-name">
                {client.name}
              </Heading>
              <div data-testid="client-status-label">{label}</div>
            </div>

            {currentTab === 'profile' && (
              <ClientProfileDeleteBtn
                client={client as never}
                currentUser={currentUser as never}
              />
            )}
          </div>

          <TabsRouter options={tabsData as never} />
        </>
      )}
    </ResponsiveWrapper>
  );
};

export default ClientDetails;
